import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { authHeaders } from '../utils/api';
import {
  Building2, Truck, Users, ClipboardList, Clock, AlertTriangle,
  TrendingUp, UserCog, Fuel, ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatCard({ icon: Icon, label, value, color, sub }) {
  const colorMap = {
    yellow: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    green: 'bg-green-500/10 text-green-400 border-green-500/20',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    orange: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    cyan: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
    red: 'bg-red-500/10 text-red-400 border-red-500/20',
    gray: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
  };
  const classes = colorMap[color] || colorMap.gray;
  return (
    <div className={`bg-[#111] border border-gray-800 rounded-xl p-4 hover:border-gray-700 transition-colors`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-gray-400 text-sm">{label}</span>
        <div className={`p-2 rounded-lg ${classes}`}><Icon size={18} /></div>
      </div>
      <p className="text-white text-2xl font-bold">{value}</p>
      {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
    </div>
  );
}

export default function MasterOverview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchOverview(); }, []);

  const fetchOverview = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tenants/overview/all`, { headers: authHeaders() });
      if (response.ok) setData(await response.json());
      else toast.error('Erro ao carregar visao geral');
    } catch { toast.error('Erro de conexao'); }
    finally { setLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-10 h-10 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!data) return null;

  const { tenants, totals } = data;
  const activeTenants = tenants.filter(t => t.status === 'active');
  const inactiveTenants = tenants.filter(t => t.status === 'inactive');

  // Top 5 tenants by total hours
  const topByHours = [...tenants]
    .filter(t => t.status === 'active')
    .sort((a, b) => b.total_hours - a.total_hours)
    .slice(0, 5);

  // Tenants with alerts
  const withAlerts = tenants.filter(t => t.maintenance_alerts > 0 && t.status === 'active');

  return (
    <div data-testid="master-overview" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white">Visao Geral</h1>
          <p className="text-gray-400 text-sm mt-1">Metricas consolidadas de todas as empresas</p>
        </div>
        <button onClick={() => navigate('/tenants')} data-testid="go-to-tenants-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-[#111] border border-gray-700 hover:border-yellow-500/50 text-gray-300 hover:text-white rounded-lg transition-colors text-sm">
          <Building2 size={16} /> Gerenciar Empresas <ArrowRight size={14} />
        </button>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Building2} label="Empresas Ativas" value={activeTenants.length} color="yellow" sub={inactiveTenants.length > 0 ? `${inactiveTenants.length} inativa(s)` : undefined} />
        <StatCard icon={Truck} label="Maquinas" value={totals.machines} color="blue" />
        <StatCard icon={Users} label="Operadores" value={totals.operators} color="green" />
        <StatCard icon={Clock} label="Horas Totais" value={totals.total_hours.toLocaleString('pt-BR')} color="purple" />
        <StatCard icon={ClipboardList} label="O.S. Abertas" value={totals.open_orders} color="orange" />
        <StatCard icon={TrendingUp} label="O.S. Fechadas" value={totals.closed_orders} color="emerald" />
        <StatCard icon={UserCog} label="Usuarios" value={totals.users} color="cyan" />
        <StatCard icon={AlertTriangle} label="Alertas Manutencao" value={totals.maintenance_alerts} color={totals.maintenance_alerts > 0 ? 'red' : 'gray'} />
      </div>

      {/* Rankings Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top by Hours */}
        <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-800">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <TrendingUp size={16} className="text-yellow-500" /> Top Empresas por Horas
            </h2>
          </div>
          <div className="divide-y divide-gray-800/50">
            {topByHours.length === 0 && (
              <p className="text-gray-500 text-sm p-5 text-center">Nenhuma empresa ativa</p>
            )}
            {topByHours.map((t, i) => (
              <div key={t.id} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-800/20 transition-colors">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                  i === 0 ? 'bg-yellow-500 text-black' : i === 1 ? 'bg-gray-400 text-black' : i === 2 ? 'bg-orange-600 text-white' : 'bg-gray-700 text-gray-400'
                }`}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{t.name}</p>
                  <p className="text-gray-500 text-xs">{t.machines} maq. / {t.operators} op.</p>
                </div>
                <span className="text-yellow-500 font-semibold text-sm shrink-0">{t.total_hours.toLocaleString('pt-BR')}h</span>
              </div>
            ))}
          </div>
        </div>

        {/* Alerts */}
        <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-800">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <AlertTriangle size={16} className="text-red-400" /> Empresas com Alertas
            </h2>
          </div>
          <div className="divide-y divide-gray-800/50">
            {withAlerts.length === 0 ? (
              <div className="p-5 text-center">
                <p className="text-green-400 text-sm font-medium">Nenhum alerta ativo</p>
                <p className="text-gray-500 text-xs mt-1">Todas as empresas estao em dia</p>
              </div>
            ) : (
              withAlerts.sort((a, b) => b.maintenance_alerts - a.maintenance_alerts).map(t => (
                <div key={t.id} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-800/20 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0">
                    <AlertTriangle size={14} className="text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{t.name}</p>
                    <p className="text-gray-500 text-xs">{t.machines} maquinas</p>
                  </div>
                  <span className="text-red-400 font-semibold text-sm shrink-0">{t.maintenance_alerts} alerta(s)</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Per-Tenant Summary (compact) */}
      <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-base font-semibold text-white">Resumo por Empresa</h2>
          <span className="text-xs text-gray-500">{activeTenants.length} ativa(s)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-gray-400">
                <th className="text-left px-5 py-3 font-medium">Empresa</th>
                <th className="text-center px-3 py-3 font-medium">Maq.</th>
                <th className="text-center px-3 py-3 font-medium">Op.</th>
                <th className="text-center px-3 py-3 font-medium">O.S.</th>
                <th className="text-center px-3 py-3 font-medium">Horas</th>
                <th className="text-center px-3 py-3 font-medium">Alertas</th>
              </tr>
            </thead>
            <tbody>
              {activeTenants.map(t => (
                <tr key={t.id} className="border-b border-gray-800/50 hover:bg-gray-800/20 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <Building2 size={14} className="text-yellow-500 shrink-0" />
                      <span className="text-white font-medium truncate">{t.name}</span>
                      {t.is_demo && <span className="text-xs px-1.5 py-0.5 bg-yellow-500/10 text-yellow-500 rounded shrink-0">Demo</span>}
                    </div>
                  </td>
                  <td className="text-center px-3 py-3 text-gray-300">{t.machines}</td>
                  <td className="text-center px-3 py-3 text-gray-300">{t.operators}</td>
                  <td className="text-center px-3 py-3 text-gray-300">{t.open_orders}<span className="text-gray-600">/{t.closed_orders}</span></td>
                  <td className="text-center px-3 py-3 text-gray-300">{t.total_hours.toLocaleString('pt-BR')}</td>
                  <td className="text-center px-3 py-3">
                    {t.maintenance_alerts > 0
                      ? <span className="inline-flex items-center gap-1 text-red-400 text-xs font-medium"><AlertTriangle size={12} /> {t.maintenance_alerts}</span>
                      : <span className="text-gray-600">0</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
